import { ROUTES } from '@common/enums'
import {
  CClient,
  handleRedirectionWithSignupSource,
  omit,
  queryStringToObject,
} from '@common/utils'
import FastComments from '@components/common/fast-comments'
import Footer from '@components/common/footer'
import { LazyAPIImage, LazyImage } from '@components/common/image'
import Navbar from '@components/common/navbar'
import RenderRichTextContent from '@components/common/render-rich-text-content'
import Section from '@components/common/section'
import Spinner from '@components/common/spinner'
import QuizList from '@components/quizzes/quiz-list'
import { useSessionContext } from '@contexts/session'
import useResolution from '@hooks/useResolution'
import { COMPETITION_TYPES } from '@lib/constants/competitions'
import {
  FALLBACK_QUIZ_DETAILS_PAGE_META_IMAGE,
  QUIZZES_IMAGE_PATH,
  QUIZ_DIFFICULTY,
  QUIZ_STATE,
} from '@lib/constants/quizzes'
import { quizzesListSeo } from '@lib/seo'
import {
  CompetitionEventDetailsProps,
  CompetititonsListType,
} from '@lib/types/competitions'
import { QuizLinkedEntityType, QuizStateType } from '@lib/types/quizzes'
import { getServerSideRedirectResult } from '@lib/utils/common'
import {
  getFormattedPlayCount,
  getGradeRange,
  getQuizState,
  onCreateAccountToSave,
  onQuizPlay,
} from '@lib/utils/quizzes'
import classNames from 'classnames'
import { userFetcher } from 'data-fetchers/common/user-fetcher'
import { GetServerSideProps } from 'next'
import { NextSeo } from 'next-seo'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { parseCookies } from 'nookies'
import { Line } from 'rc-progress'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronLeft } from 'react-feather'
import {
  FacebookIcon,
  FacebookShareButton,
  TelegramIcon,
  TelegramShareButton,
  WhatsappIcon,
  WhatsappShareButton,
} from 'react-share'

type QuizStatProps = {
  image: 'points' | 'grade-range' | 'play-count' | 'question-count' | 'rank'
  stat: string
  imageClasses?: string
  statClasses?: string
}

export const QuizStat = ({
  image,
  stat,
  imageClasses = '',
  statClasses = '',
}: QuizStatProps) => (
  <div className="flex items-center space-x-3">
    <LazyImage
      path={`${QUIZZES_IMAGE_PATH}details-page/`}
      name={image}
      className={imageClasses}
      alt="question mark"
    />
    <span className={statClasses}>{stat}</span>
  </div>
)

type Props = {
  quizEventDetails: CompetitionEventDetailsProps<QuizLinkedEntityType>
  recommendedQuizzes: CompetititonsListType<QuizLinkedEntityType>
}

const Quiz = ({
  quizEventDetails: {
    phases,
    title,
    minGrade,
    maxGrade,
    slug,
    sections,
    id: quizId,
    metaTitle,
    metaDescription,
    ogImage,
  },
  recommendedQuizzes,
}: Props) => {
  const { loggedIn } = useSessionContext()
  const [isLoading, setIsLoading] = useState(false)
  const [imageContainerHeight, setImageContainerHeight] = useState('auto')

  const contentRef = useRef<HTMLDivElement>(null)
  const router = useRouter()
  const selectedDiv = router.query.div as string
  const {
    sizes: { sm, md, lg },
  } = useResolution()

  const {
    coverImage,
    difficulty,
    quizTopics,
    outline,
    totalProblems,
    playCount,
    remainingProblems,
    totalPointsScored,
    correctSolutionCount,
    id,
    isLessonQuiz,
  } = phases[0].linkedEntity

  const quizState: QuizStateType = useMemo(
    () => getQuizState(totalProblems, remainingProblems),
    []
  )

  const quizUrl = `https://codingal.com/quizzes/${isLessonQuiz ? '' : slug}`
  const shareQuizCopy = isLessonQuiz
    ? 'I invite you to play quizzes on Codingal.'
    : `I invite you to play the quiz "${title}" on Codingal and challenge me!`

  /*
  If the quiz is completed:
    Calculate accuracy by this formula: correctSolutionCount / totalProblems
  If the quiz is incompleted:
    Calculate what percentage of quiz is completed: (totalProblems - remainingProblems) / totalProblems
  */
  const progressPercentage = (
    (100 *
      (quizState === QUIZ_STATE.INCOMPLETE
        ? totalProblems - remainingProblems
        : correctSolutionCount)) /
    totalProblems
  ).toFixed(0)

  const onPlayButtonClick = async () => {
    if (isLoading) return
    setIsLoading(true)
    const wasParticipationAPICallSuccessful = await onQuizPlay(id, slug)
    if (wasParticipationAPICallSuccessful) setIsLoading(false)
  }

  useEffect(() => {
    if (contentRef.current?.offsetHeight)
      setImageContainerHeight(contentRef.current?.offsetHeight + 'px')
  }, [contentRef.current?.offsetHeight])

  useEffect(() => {
    if (selectedDiv) {
      const element = document.getElementById(selectedDiv)
      element.scrollIntoView({ behavior: 'smooth' })
      // Remove the selected div immediately after scrolling to it
      // And the route is shallow to no cause re-renders
      router.replace(
        {
          query: omit(router.query as any, ['div', 'quizName']) as any,
        },
        null,
        { shallow: true, scroll: false }
      )
    }
  }, [selectedDiv])

  return (
    <>
      <NextSeo
        title={metaTitle || quizzesListSeo.title}
        description={metaDescription || quizzesListSeo.description}
        openGraph={{
          title: metaTitle || quizzesListSeo.title,
          description: metaDescription || quizzesListSeo.description,
          type: 'website',
          url: `https://www.codingal.com/quizzes/${slug}/`,
          ['site_name']: 'Codingal',
          images: [
            {
              url: ogImage || FALLBACK_QUIZ_DETAILS_PAGE_META_IMAGE,
              alt: `Codingal ${title}`,
            },
          ],
        }}
      />
      <Navbar next={router.asPath} />
      <div className="pb-10 bg-grey-300 lg:py-5">
        <div className="grid max-w-6xl gap-6 py-6 mx-auto lg:py-0 lg:pb-6">
          <div className="flex">
            <Link href="/quizzes">
              <a className="flex items-start justify-start px-2 text-lg cursor-pointer text-orange hover:underline">
                <ChevronLeft />
                Back to quizzes
              </a>
            </Link>
          </div>
          <div className="grid mx-5 bg-white rounded-md lg:grid-cols-2">
            <div
              style={{ height: sm || md || lg ? 'auto' : imageContainerHeight }}
            >
              <LazyAPIImage
                src={coverImage}
                alt={`${title} cover image`}
                className="object-cover w-full h-64 rounded-t-md lg:rounded-l-md lg:rounded-t-none lg:h-full"
              />
            </div>
            <div
              className="flex flex-col items-start p-8 space-y-5 overflow-auto"
              ref={contentRef}
            >
              <div className="flex flex-col space-y-2">
                <div className="flex flex-col flex-wrap items-start space-y-2 lg:flex-row lg:space-y-0 lg:items-center">
                  <h1 className="text-2xl leading-tight font-600 lg:mr-2">
                    {title}
                  </h1>
                  <p
                    className={classNames('rounded-md px-2', {
                      'bg-green-50': difficulty === QUIZ_DIFFICULTY.BEGINNER,
                      'bg-yellow-100':
                        difficulty === QUIZ_DIFFICULTY.INTERMEDIATE,
                      'bg-red-100': difficulty === QUIZ_DIFFICULTY.EXPERT,
                    })}
                  >
                    {difficulty} Level
                  </p>
                </div>
                <div className="flex flex-wrap items-center space-x-1 text-sm">
                  {quizTopics.map((topic) => (
                    <p
                      key={topic}
                      className="px-2 my-1 border rounded-md bg-grey-200 border-grey-400"
                    >
                      {topic}
                    </p>
                  ))}
                </div>
              </div>
              <RenderRichTextContent
                content={outline}
                className="leading-tight"
              />
              <div className="flex flex-col space-y-2 lg:flex-row lg:space-y-0 lg:space-x-6">
                <QuizStat
                  image="question-count"
                  stat={`No. Questions: ${totalProblems}`}
                  imageClasses="w-6 h-6"
                />
                <QuizStat
                  image="play-count"
                  stat={`Plays: ${getFormattedPlayCount(playCount)}`}
                  imageClasses="w-6 h-6"
                />
                <QuizStat
                  image="grade-range"
                  stat={`Grade ${getGradeRange(minGrade, maxGrade)}`}
                  imageClasses="w-6 h-6"
                />
              </div>
              {quizState === QUIZ_STATE.NOT_STARTED && (
                <button
                  className="btn btn-orange"
                  onClick={onPlayButtonClick}
                  disabled={isLoading}
                >
                  {isLoading ? <Spinner classes="w-5" /> : 'Play Now'}
                </button>
              )}
              {quizState !== QUIZ_STATE.NOT_STARTED && (
                <>
                  <div className="relative">
                    <Line
                      percent={Number.parseInt(progressPercentage)}
                      strokeWidth={8}
                      trailWidth={8}
                      strokeColor="#66B86B"
                      trailColor="#66B86B8F"
                      strokeLinecap="round"
                      className="w-full h-6 rounded-full lg:max-w-md"
                    />
                    <span className="absolute top-0 px-4 mt-px text-sm text-white font-600">
                      {quizState === QUIZ_STATE.INCOMPLETE
                        ? `${remainingProblems} question${
                            remainingProblems > 1 ? 's' : ''
                          } remaining`
                        : `Accuracy ${progressPercentage}%`}
                    </span>
                  </div>
                  <QuizStat
                    image="points"
                    stat={`Points: ${totalPointsScored}`}
                    imageClasses="w-10 h-10"
                    statClasses="font-600"
                  />
                  <div className="flex flex-col items-start space-y-5 lg:flex-row lg:space-y-0 lg:space-x-5">
                    {quizState === QUIZ_STATE.INCOMPLETE && (
                      <>
                        <Link href={`/quizzes/${slug}/arena/`}>
                          <a className="btn btn-orange">Resume</a>
                        </Link>
                        {loggedIn ? (
                          <button
                            onClick={onPlayButtonClick}
                            className="btn text-orange hover:bg-orange-200 active:bg-orange-300"
                            disabled={isLoading}
                          >
                            {isLoading ? (
                              <Spinner classes="w-5" />
                            ) : (
                              'Play Again'
                            )}
                          </button>
                        ) : (
                          <button
                            onClick={onCreateAccountToSave}
                            disabled={isLoading}
                            className="btn text-orange hover:bg-orange-200 active:bg-orange-300"
                          >
                            Create account to save
                          </button>
                        )}
                      </>
                    )}
                    {quizState === QUIZ_STATE.COMPLETED && (
                      <>
                        <button
                          onClick={onPlayButtonClick}
                          className="btn btn-orange"
                          disabled={isLoading}
                        >
                          {isLoading ? <Spinner classes="w-5" /> : 'Play Again'}
                        </button>
                        {!loggedIn && (
                          <button
                            onClick={onCreateAccountToSave}
                            disabled={isLoading}
                            className="btn text-orange hover:bg-orange-200 active:bg-orange-300"
                          >
                            Create account to save
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
              <div className="flex flex-col items-start space-y-2 lg:flex-row lg:space-y-0 lg:space-x-2">
                <span className="lg:my-auto">Share</span>
                <div className="flex -ml-1 space-x-1 lg:m-0">
                  <FacebookShareButton url={quizUrl} quote={shareQuizCopy}>
                    <FacebookIcon
                      borderRadius={40}
                      className="w-10 h-10 p-1 duration-100 border border-white rounded-full hover:border-orange focus:outline-none"
                    />
                  </FacebookShareButton>
                  <WhatsappShareButton
                    url={quizUrl}
                    title={`${shareQuizCopy} Play now -`}
                  >
                    <WhatsappIcon
                      borderRadius={40}
                      className="w-10 h-10 p-1 duration-100 border border-white rounded-full hover:border-orange focus:outline-none"
                    />
                  </WhatsappShareButton>
                  <TelegramShareButton url={quizUrl} title={shareQuizCopy}>
                    <TelegramIcon
                      borderRadius={40}
                      className="w-10 h-10 p-1 duration-100 border border-white rounded-full hover:border-orange focus:outline-none"
                    />
                  </TelegramShareButton>
                </div>
              </div>
            </div>
          </div>
          <a
            onClick={() =>
              handleRedirectionWithSignupSource(
                router.asPath,
                '/trial-class/booking'
              )
            }
            className="relative flex flex-col mx-5 bg-white rounded-md cursor-pointer"
          >
            <LazyImage
              path={`${QUIZZES_IMAGE_PATH}details-page/`}
              name="book-trial-class-sm"
              alt="Students studying coding and enjoying"
              className="lg:hidden"
            />
            <LazyImage
              path={`${QUIZZES_IMAGE_PATH}details-page/`}
              name="book-trial-class-lg"
              className="hidden lg:block"
              alt="Students studying coding and enjoying"
            />
            <div className="flex flex-col items-start p-8 space-y-5 lg:absolute lg:top-0 lg:right-0 lg:w-108 lg:mt-12 lg:mr-20">
              <p className="text-2xl leading-tight">
                Want to learn coding and ace these quizzes?
              </p>
              <button className="btn text-orange hover:bg-orange-100 active:bg-orange-200">
                Try a free lesson
              </button>
            </div>
          </a>

          <div className="flex flex-col p-8 mx-5 bg-white rounded-lg">
            {sections.map((section) => (
              <Section
                title={section.title}
                key={section.slug}
                id={section.slug}
              >
                <RenderRichTextContent
                  className="max-w-screen-md text-grey"
                  content={section.content}
                  options={{
                    ADD_TAGS: ['iframe'],
                    ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder'],
                  }}
                />
              </Section>
            ))}
            <div id="comments">
              <FastComments
                loginCallback={() => {
                  const [plainPathname, queryString] = router.asPath.split('?')
                  router.replace({
                    pathname: '/login',
                    query: {
                      next: plainPathname,
                      div: 'comments',
                      ...queryStringToObject(queryString),
                    },
                  })
                }}
                uniqueUrl={`quizzes/${quizId}`}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="bg-blue-100 bg-opacity-25">
        <div className="flex flex-col text-grey container justify-center pt-10 mx-auto px-5 py-20 md:px-10">
          <h2 className="text-xl md:text-3xl font-700 mb-6">
            More recommended quizzes for you
          </h2>
          <QuizList quizzes={recommendedQuizzes} />
        </div>
      </div>

      <Footer />
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const token = parseCookies(ctx).token
  const response = await CClient(
    `/api/v1/competitions/${ctx.params?.quizName}/`,
    {
      method: 'GET',
      headers: {
        Authorization: token ? `Token ${token}` : null,
        cookie: ctx.req.headers.cookie,
      },
    }
  )

  if (response.status === 404) return { notFound: true }

  if (!response.ok) {
    return getServerSideRedirectResult(ctx, `/${response.status}`)
  }

  const quizEventDetails: CompetitionEventDetailsProps<QuizLinkedEntityType> = await response.json()

  /** Redirect user to quiz list page if current quiz is a lesson quiz and the user is not a paid user */
  if (quizEventDetails?.phases?.[0]?.linkedEntity?.isLessonQuiz) {
    const userData = await userFetcher('/api/v1/users/me/', token, ctx)
    if (!userData.loggedIn || !userData.isPaidUser)
      return getServerSideRedirectResult(ctx, ROUTES.QUIZZES_LIST)
  }
  const quizeResponse = await CClient(
    `/api/v1/competitions/?page_size=3&page=1&competition_type=${COMPETITION_TYPES.QUIZ_CONTEST}&is_practice=True&grade=${quizEventDetails.minGrade}&grade=${quizEventDetails.maxGrade}`,
    {
      method: 'GET',
      headers: {
        Authorization: token ? `Token ${token}` : null,
        'Content-Type': 'application/json',
      },
    }
  )
  if (quizeResponse.ok) {
    const data = await quizeResponse.json()
    const categorizedCompetitions: CompetititonsListType<QuizLinkedEntityType> =
      data.results
    return {
      props: { quizEventDetails, recommendedQuizzes: categorizedCompetitions },
    }
  }
  return {
    props: { quizEventDetails },
  }
}

export default Quiz
